import { useEffect, useRef, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Copy, Download, History, PanelRightClose, Pencil, RotateCcw, WandSparkles } from "lucide-react";
import { toast } from "sonner";
import { Markdown } from "@/components/drift/ai/Markdown";
import { downloadText, slugFile } from "@/components/drift/ai/stream";
import { Segmented } from "@/components/drift/common";
import { Chip } from "@/components/drift/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { fmtTime } from "@/lib/format";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   Canvas: the document the assistant writes, beside the chat. Preview or
   edit, step through versions, restore, copy, download – and ask for
   changes with one click (those go back through the chat).
   ------------------------------------------------------------------ */

const DOC_KIND = { tilbod: "Tilbodstekst", epost: "E-post", rapport: "Rapport", sjekkliste: "Sjekkliste", referat: "Møtereferat", prosedyre: "Prosedyre", sosiale_medium: "Innlegg", notat: "Notat", anna: "Dokument" };

const QUICK = [
  ["Kortare", "Gjer dokumentet i canvas kortare. Behald det viktigaste og strukturen."],
  ["Meir formelt", "Gjer dokumentet i canvas meir formelt i tonen, utan å endre innhaldet."],
  ["Enklare språk", "Skriv dokumentet i canvas om med enklare og tydelegare språk."],
  ["Punktliste", "Gjer om dei lengste avsnitta i dokumentet i canvas til punktlister der det gir betre oversikt."],
  ["Bokmål", "Omset dokumentet i canvas til bokmål. Behald struktur og innhald."],
  ["Korrektur", "Les korrektur på dokumentet i canvas: rett skrivefeil og uklare setningar, men ikkje endre innhaldet."],
];

const IconBtn = ({ label, onClick, children, testid, disabled }) => (
  <Tooltip delayDuration={300}>
    <TooltipTrigger asChild>
      <button type="button" onClick={onClick} disabled={disabled} className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40" aria-label={label} data-testid={testid}>{children}</button>
    </TooltipTrigger>
    <TooltipContent side="bottom" className="rounded-lg text-[12.5px]">{label}</TooltipContent>
  </Tooltip>
);

export function CanvasPanel({ canvas, versions = [], viewing, onLoadVersion, onRestore, onSave, onClose, onAsk, streaming, className }) {
  const doc = viewing || canvas;
  const latest = canvas?.version || 0;
  const isOld = !!viewing && viewing.version !== latest;
  const [mode, setMode] = useState("preview");
  const [draft, setDraft] = useState(doc?.content || "");
  const [title, setTitle] = useState(doc?.title || "");
  const [editingTitle, setEditingTitle] = useState(false);
  const [copied, setCopied] = useState(false);
  const taRef = useRef(null);

  // New version (from the assistant, a restore or a save) → reset the draft.
  useEffect(() => { setDraft(doc?.content || ""); setTitle(doc?.title || ""); setEditingTitle(false); }, [doc?.version, doc?.content, doc?.title]);
  useEffect(() => { if (isOld) setMode("preview"); }, [isOld]);

  if (!doc) return null;
  const dirty = draft !== (doc.content || "") || title !== (doc.title || "");
  const words = (draft.trim().match(/\S+/g) || []).length;

  const save = () => { if (!dirty || isOld) return; onSave(draft, title.trim() || doc.title); setMode("preview"); };
  const copy = () => navigator.clipboard?.writeText(draft).then(() => { setCopied(true); toast.success("Dokumentet er kopiert"); setTimeout(() => setCopied(false), 1500); });
  const download = () => downloadText(`${slugFile(doc.title)}.md`, draft);
  const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); save(); } };

  return (
    <section className={cn("flex h-full min-h-0 flex-col bg-background/60", className)} data-testid="ai-canvas" aria-label="Canvas" data-version={doc.version}>
      {/* Header */}
      <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2 sm:px-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Chip className="shrink-0">{DOC_KIND[doc.kind] || "Dokument"}</Chip>
            {editingTitle && !isOld ? (
              <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => setEditingTitle(false)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingTitle(false); }} className="h-7 min-w-0 flex-1 rounded-md bg-card px-2 text-[14px] font-medium outline-none ring-2 ring-ring" aria-label="Tittel på dokumentet" data-testid="ai-canvas-title-input" />
            ) : (
              <button type="button" onClick={() => !isOld && setEditingTitle(true)} className={cn("group inline-flex min-w-0 items-center gap-1.5 rounded-md text-left text-[14px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", !isOld && "hover:text-foreground")} title={title} data-testid="ai-canvas-title">
                <span className="truncate">{title}</span>
                {!isOld && <Pencil className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={1.75} aria-hidden />}
              </button>
            )}
          </div>
          <p className="mt-0.5 truncate text-[11.5px] text-muted-foreground" data-testid="ai-canvas-meta">
            {words} ord · {doc.source === "user" ? "redigert av deg" : "skrive av assistenten"} {doc.updated_at ? `kl. ${fmtTime(doc.updated_at)}` : ""}{doc.note ? ` · ${doc.note}` : ""}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {latest > 1 && (
            <div className="mr-1 hidden items-center rounded-lg bg-secondary/80 p-0.5 sm:flex" data-testid="ai-canvas-versions">
              <button type="button" disabled={doc.version <= 1} onClick={() => onLoadVersion(doc.version - 1)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-card hover:text-foreground disabled:opacity-35" aria-label="Førre versjon" data-testid="ai-canvas-prev-version"><ChevronLeft className="h-4 w-4" strokeWidth={1.75} /></button>
              <span className="px-1 font-mono text-[11.5px] text-muted-foreground tabular">v{doc.version}/{latest}</span>
              <button type="button" disabled={doc.version >= latest} onClick={() => onLoadVersion(doc.version + 1)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-card hover:text-foreground disabled:opacity-35" aria-label="Neste versjon" data-testid="ai-canvas-next-version"><ChevronRight className="h-4 w-4" strokeWidth={1.75} /></button>
            </div>
          )}
          <IconBtn label={copied ? "Kopiert" : "Kopier"} onClick={copy} testid="ai-canvas-copy">{copied ? <Check className="h-4 w-4" strokeWidth={2} /> : <Copy className="h-4 w-4" strokeWidth={1.75} />}</IconBtn>
          <IconBtn label="Last ned (.md)" onClick={download} testid="ai-canvas-download"><Download className="h-4 w-4" strokeWidth={1.75} /></IconBtn>
          <IconBtn label="Lukk canvas" onClick={onClose} testid="ai-canvas-close"><PanelRightClose className="h-4 w-4" strokeWidth={1.75} /></IconBtn>
        </div>
      </header>

      {/* Old version banner */}
      {isOld && (
        <div className="flex items-center gap-3 border-b border-border/70 bg-secondary/60 px-4 py-2 text-[12.5px]" data-testid="ai-canvas-old-banner">
          <History className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-hidden />
          <span className="min-w-0 flex-1 truncate text-muted-foreground">Du ser versjon {doc.version} av {latest}.</span>
          <Button size="sm" variant="outline" className="h-7 rounded-lg bg-card text-[12px]" onClick={() => onRestore(doc)} data-testid="ai-canvas-restore"><RotateCcw className="mr-1.5 h-3 w-3" strokeWidth={1.75} /> Gjenopprett</Button>
          <Button size="sm" variant="ghost" className="h-7 rounded-lg text-[12px]" onClick={() => onLoadVersion(latest)} data-testid="ai-canvas-to-latest">Til siste</Button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 sm:px-4">
        <Segmented size="sm" value={mode} onChange={setMode} options={[["preview", "Førehandsvis"], ["edit", "Rediger"]]} testid="ai-canvas-mode" />
        {dirty && !isOld && (
          <div className="flex items-center gap-1.5" data-testid="ai-canvas-dirty">
            <Button size="sm" variant="ghost" className="h-8 rounded-lg text-[12.5px]" onClick={() => { setDraft(doc.content || ""); setTitle(doc.title || ""); }} data-testid="ai-canvas-discard">Forkast</Button>
            <Button size="sm" className="h-8 rounded-lg bg-brand text-[12.5px] text-brand-foreground hover:bg-brand-dark" onClick={save} data-testid="ai-canvas-save">Lagre <span className="kbd ml-2 border-brand-foreground/30 bg-transparent text-brand-foreground/80">⌘S</span></Button>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin" data-testid="ai-canvas-body">
        <div className="mx-auto w-full max-w-[720px] px-5 pb-10 pt-2 sm:px-8">
          {mode === "edit" && !isOld ? (
            <textarea
              ref={taRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              spellCheck
              className="min-h-[60vh] w-full resize-none bg-transparent font-mono text-[13.5px] leading-[1.7] text-foreground outline-none"
              aria-label="Rediger dokumentet"
              data-testid="ai-canvas-editor"
            />
          ) : (
            <div className={cn("rounded-xl", streaming && !isOld && "opacity-90")}>
              <Markdown text={draft} doc />
            </div>
          )}
        </div>
      </div>

      {/* Quick AI actions */}
      {!isOld && (
        <footer className="border-t border-border/70 px-3 py-2.5 sm:px-4" data-testid="ai-canvas-quick">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
            <span className="mr-1 inline-flex shrink-0 items-center gap-1 text-[11.5px] text-muted-foreground"><WandSparkles className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden /> Be om</span>
            {QUICK.map(([label, prompt]) => (
              <button key={label} type="button" disabled={streaming || dirty} onClick={() => onAsk(prompt)} className="inline-flex h-7 shrink-0 items-center rounded-full bg-card px-2.5 text-[12px] font-medium shadow-panel transition-[background-color] duration-150 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40" data-testid={`ai-canvas-quick-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</button>
            ))}
          </div>
          {dirty && <p className="mt-1.5 text-[11.5px] text-muted-foreground">Lagre endringane dine før du ber assistenten om meir.</p>}
        </footer>
      )}
    </section>
  );
}
